import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import StippledBackground from '@/components/StippledBackground'
import Button from '@/components/ui/Button'
import { useThemeMode } from '@/contexts/ThemeContext'
import { useUser } from '@/hooks/useUser'
import useIsMobile from '@/hooks/useIsMobile'
import { api } from '@/lib/api/client'
import { theme as defaultTheme } from '@/lib/theme'

interface IdentityData {
  id: string
  userId: string
  sessionId?: string
  name: string
  content: string
  type: 'identity'
  language: string
  description?: string
  updatedAt: string
}

interface SharedIdentitySnapshot {
  data: IdentityData
  viewCount: number
  importCount: number
  sharedAt: string
}

// Simplified TopNav for shared identity page
interface SimpleTopNavProps {
  onThemeToggle: () => void
  onLoginClick: () => void
  isGuestMode: boolean
}

function SimpleTopNav({
  onThemeToggle,
  isGuestMode,
  onLoginClick,
}: SimpleTopNavProps) {
  const { isDark, colors } = useThemeMode()

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: '40px',
        backgroundColor: colors.navBg,
        borderBottom: `1px solid ${colors.navBorder}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 12px',
        zIndex: 1000,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        fontFamily: 'var(--font-departure-mono)',
      }}
    >
      {/* Logo */}
      <a
        href="https://honcho.chat"
        target="_blank"
        rel="noopener noreferrer"
        style={{
          fontSize: '14px',
          fontWeight: 'bold',
          color: colors.navItemText,
          letterSpacing: '1px',
          textDecoration: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}
      >
        <svg width="20" height="18" viewBox="0 0 87 77" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginTop: '-2px' }}>
          <path d="M55.29 0H38.77V6.89H55.29V0Z" fill="currentColor"/>
          <path d="M65.95 6.89H55.29V13.69H65.95V6.89Z" fill="currentColor"/>
          <path d="M72.66 13.69H65.95V20.4H72.66V13.69Z" fill="currentColor"/>
          <path d="M79.54 20.4H72.66V30.17H79.54V20.4Z" fill="currentColor"/>
          <path d="M86.33 30.16H79.54V46.54H86.33V30.16Z" fill="currentColor"/>
          <path d="M79.54 46.55H72.67V56.32H79.54V46.55Z" fill="currentColor"/>
          <path d="M72.67 56.31H65.95V63.11H72.67V56.31Z" fill="currentColor"/>
          <path d="M65.95 63.11H55.29V69.96H65.95V63.11Z" fill="currentColor"/>
          <path d="M55.29 69.96H35.84V76.65H55.29V69.96Z" fill="currentColor"/>
          <path d="M35.84 63.11H25.18V69.96H35.84V63.11Z" fill="currentColor"/>
          <path d="M25.18 56.31H18.39V63.11H25.18V56.31Z" fill="currentColor"/>
          <path d="M18.39 46.55H11.52V56.32H18.39V46.55Z" fill="currentColor"/>
          <path d="M4.87 33.97V20.4H0V40.8H4.87V46.55H11.52V33.97H4.87Z" fill="currentColor"/>
          <path d="M18.31 27.17H11.52V33.96H18.31V27.17Z" fill="currentColor"/>
          <path d="M38.77 6.89H18.39V13.69H38.77V6.89Z" fill="currentColor"/>
          <path d="M45.5 13.69H38.79V20.4H45.5V13.69Z" fill="currentColor"/>
          <path d="M18.32 27.17H38.79V20.4H18.39V13.69H4.87V20.4H18.32V27.17Z" fill="currentColor"/>
          <path d="M38.79 30.16H31.98V36.87H38.79V30.16Z" fill="currentColor"/>
          <path d="M59.09 30.16H52.28V36.87H59.09V30.16Z" fill="currentColor"/>
          <path d="M59.16 50.47H31.98V56.31H59.16V50.47Z" fill="currentColor"/>
        </svg>
        Honcho Chat
      </a>

      {/* Right side controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {/* Login button for guest users */}
        {isGuestMode && (
          <button
            onClick={onLoginClick}
            style={{
              padding: '4px 12px',
              backgroundColor: colors.visualAccent1,
              color: '#ffffff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.85'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1'
            }}
          >
            Sign in to Import
          </button>
        )}

        {/* Theme Toggle */}
        <button
          onClick={onThemeToggle}
          style={{
            padding: '4px 8px',
            background: 'transparent',
            border: `1px solid ${colors.primaryBorder}`,
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            color: colors.primaryText,
            fontSize: '14px',
            transition: 'all 0.15s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = colors.secondaryBg
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent'
          }}
        >
          {isDark ? '🌙' : '☀︎'}
        </button>
      </div>
    </div>
  )
}

// Message Bubble Component
interface MessageBubbleProps {
  children: React.ReactNode
  sender: 'user' | 'assistant'
  timestamp?: string
}

function MessageBubble({ children, sender, timestamp }: MessageBubbleProps) {
  const { colors } = useThemeMode()

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        marginBottom: '8px',
        position: 'relative',
      }}
    >
      <div
        style={{
          padding: defaultTheme.spacing.md,
          backgroundColor:
            sender === 'user' ? colors.userMsgBgAlpha : colors.assistantMsgBg,
          borderRadius: '10px',
          borderLeft:
            sender === 'assistant'
              ? `3px solid ${colors.assistantMsgAccent}`
              : 'none',
          borderRight:
            sender === 'user' ? `3px solid ${colors.userMsgAccent}` : 'none',
          color: colors.primaryText,
          fontFamily: 'var(--font-departure-mono)',
          fontSize: '13px',
          lineHeight: '1.6',
        }}
      >
        {children}
      </div>
      {timestamp && (
        <div
          style={{
            fontSize: defaultTheme.fontSize.xs,
            color: colors.tertiaryText,
            marginTop: defaultTheme.spacing.xs,
            textAlign: 'right',
          }}
        >
          {timestamp}
        </div>
      )}
    </div>
  )
}

export default function SharedIdentityPage() {
  const { id } = useParams<{ id: string }>()
  const { colors, toggleTheme } = useThemeMode()
  const { user } = useUser()
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const [identitySnapshot, setIdentitySnapshot] =
    useState<SharedIdentitySnapshot | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [importing, setImporting] = useState(false)
  const [importSuccess, setImportSuccess] = useState(false)
  const [showJson, setShowJson] = useState(false)

  useEffect(() => {
    if (!id) return

    async function fetchSharedIdentity() {
      try {
        setLoading(true)
        const apiUrl = import.meta.env.VITE_API_URL
        const response = await fetch(`${apiUrl}/api/identities/shared/${id}`)

        if (!response.ok) {
          if (response.status === 404) {
            setError('Identity not found')
          } else {
            setError('Failed to load identity')
          }
          return
        }

        const data = await response.json()
        setIdentitySnapshot(data)
      } catch (err) {
        console.error('Error fetching shared identity:', err)
        setError('Failed to load identity')
      } finally {
        setLoading(false)
      }
    }

    fetchSharedIdentity()
  }, [id])

  const handleImport = async () => {
    if (!user) {
      // Redirect to login with return URL
      navigate(`/auth/login?redirect=/identities/shared/${id}`)
      return
    }

    setImporting(true)
    try {
      const { error: importError } = await api.api.identities.import({ id: id! }).post()

      if (importError) {
        console.error('Import error:', importError)
        setError('Failed to import identity')
        return
      }

      setImportSuccess(true)

      // Redirect to home after successful import
      setTimeout(() => {
        navigate('/')
      }, 2000)
    } catch (err) {
      console.error('Error importing identity:', err)
      setError('Failed to import identity')
    } finally {
      setImporting(false)
    }
  }

  const handleLoginClick = () => {
    navigate(`/auth/login?redirect=/identities/shared/${id}`)
  }

  if (loading) {
    return (
      <div
        style={{
          position: 'relative',
          display: 'flex',
          minHeight: '100vh',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'var(--font-departure-mono)',
        }}
      >
        <StippledBackground />
        <SimpleTopNav
          onThemeToggle={toggleTheme}
          isGuestMode={!user}
          onLoginClick={handleLoginClick}
        />
        <div
          style={{
            fontSize: defaultTheme.fontSize.lg,
            color: colors.primaryText,
            zIndex: 1,
          }}
        >
          Loading identity... 👤
        </div>
      </div>
    )
  }

  if (error || !identitySnapshot) {
    return (
      <div
        style={{
          position: 'relative',
          display: 'flex',
          minHeight: '100vh',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'var(--font-departure-mono)',
        }}
      >
        <StippledBackground />
        <SimpleTopNav
          onThemeToggle={toggleTheme}
          isGuestMode={!user}
          onLoginClick={handleLoginClick}
        />
        <div
          style={{
            fontSize: defaultTheme.fontSize.lg,
            color: colors.primaryText,
            zIndex: 1,
          }}
        >
          {error || 'Identity not found'} 😢
        </div>
      </div>
    )
  }

  // Parse identity content to show preview
  let identityData: any = {}
  try {
    identityData = JSON.parse(identitySnapshot.data.content)
  } catch (e) {
    console.error('Failed to parse identity:', e)
  }

  return (
    <div
      style={{
        position: 'relative',
        minHeight: '100vh',
        fontFamily: 'var(--font-departure-mono)',
      }}
    >
      {/* Animated stars background */}
      <StippledBackground />

      {/* Top Navigation */}
      <SimpleTopNav
        onThemeToggle={toggleTheme}
        isGuestMode={!user}
        onLoginClick={handleLoginClick}
      />

      {/* Main Chat Window */}
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          paddingTop: '60px',
          paddingBottom: defaultTheme.spacing.xl,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          minHeight: '100vh',
        }}
      >
        <div
          style={{
            maxWidth: '1000px',
            width: '90%',
            margin: `${defaultTheme.spacing.xl} auto`,
            backgroundColor: colors.primaryBg,
            border: `2px solid ${colors.primaryBorder}`,
            borderRadius: '10px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
            overflow: 'hidden',
          }}
        >
          {/* Chat Header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: colors.secondaryBg,
              borderBottom: `1px solid ${colors.primaryBorder}`,
              padding: defaultTheme.spacing.md,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: defaultTheme.spacing.sm,
              }}
            >
              <div style={{ display: 'flex', gap: '8px' }}>
                <div
                  style={{
                    width: '14px',
                    height: '14px',
                    borderRadius: '50%',
                    backgroundColor: colors.windowControlBg,
                    border: `1px solid ${colors.windowBorder}`,
                  }}
                />
                <div
                  style={{
                    width: '14px',
                    height: '14px',
                    borderRadius: '50%',
                    backgroundColor: colors.windowControlBg,
                    border: `1px solid ${colors.windowBorder}`,
                  }}
                />
                <div
                  style={{
                    width: '14px',
                    height: '14px',
                    borderRadius: '50%',
                    backgroundColor: colors.windowControlBg,
                    border: `1px solid ${colors.windowBorder}`,
                  }}
                />
              </div>
              <span
                style={{
                  fontSize: defaultTheme.fontSize.md,
                  fontWeight: '500',
                  color: colors.primaryText,
                }}
              >
                Identity Preview 👤
              </span>
            </div>
          </div>

          {/* Messages Area */}
          <div
            style={{
              padding: '12px',
              maxHeight: '70vh',
              overflowY: 'auto',
            }}
          >
            {/* Welcome Message */}

            <MessageBubble sender="assistant" timestamp="just now">
              <div>
                Hey! 👋 I'm excited to show you{' '}
                <strong>{identitySnapshot.data.name}</strong>! 👤
              </div>
              <br />
              {identitySnapshot.data.description && (
                <>
                  <div>{identitySnapshot.data.description} 🎭</div>
                  <br />
                </>
              )}
              <div
                style={{
                  fontSize: defaultTheme.fontSize.sm,
                  color: colors.tertiaryText,
                }}
              >
                📊 Shared{' '}
                {new Date(identitySnapshot.sharedAt).toLocaleDateString()} • 👀{' '}
                {identitySnapshot.viewCount} views • 💾 {identitySnapshot.importCount}{' '}
                imports
              </div>
            </MessageBubble>

            <MessageBubble sender="user" timestamp="just now">
              what's an identity? 🤔
            </MessageBubble>

            <MessageBubble sender="assistant" timestamp="just now">
              <div>
                Great question! 🎯 An identity is a custom AI persona that changes how I respond to you! 🤖
              </div>
              <br />
              <div>
                Think of it like giving me different personalities, expertise, or communication styles. You can have different identities for different tasks! 🎭
              </div>
            </MessageBubble>

            <MessageBubble sender="user" timestamp="just now">
              cool! how do i use it? 💖
            </MessageBubble>

            <MessageBubble sender="assistant" timestamp="just now">
              <div>Simple! Just import it: 🎉</div>
              <br />
              {importSuccess ? (
                <div
                  style={{
                    padding: defaultTheme.spacing.md,
                    backgroundColor: colors.successBg || colors.secondaryBg,
                    border: `1px solid ${colors.successText || colors.primaryAccent}`,
                    borderRadius: defaultTheme.borderRadius.sm,
                    color: colors.successText || colors.primaryText,
                    textAlign: 'center',
                  }}
                >
                  ✅ Imported successfully! Redirecting you now... 🎊
                </div>
              ) : user ? (
                <Button
                  onClick={handleImport}
                  disabled={importing}
                  isLoading={importing}
                  style={{
                    backgroundColor: colors.primaryAccent,
                    color: colors.folderSelectedText,
                    fontSize: defaultTheme.fontSize.md,
                    padding: `${defaultTheme.spacing.md} ${defaultTheme.spacing.lg}`,
                    width: '100%',
                    fontWeight: '600',
                  }}
                >
                  💾 Import to My Identities
                </Button>
              ) : (
                <>
                  <Button
                    onClick={handleLoginClick}
                    style={{
                      backgroundColor: colors.primaryAccent,
                      color: colors.folderSelectedText,
                      fontSize: defaultTheme.fontSize.md,
                      padding: `${defaultTheme.spacing.md} ${defaultTheme.spacing.xl}`,
                      width: '100%',
                      marginTop: defaultTheme.spacing.sm,
                      fontWeight: '600',
                    }}
                  >
                    🔐 Sign in to Import
                  </Button>
                  <div
                    style={{
                      marginTop: defaultTheme.spacing.sm,
                      fontSize: defaultTheme.fontSize.sm,
                      color: colors.tertiaryText,
                      textAlign: 'center',
                    }}
                  >
                    (You'll need to sign in first! 👤)
                  </div>
                </>
              )}
            </MessageBubble>

            <MessageBubble sender="user" timestamp="just now">
              can i see what's inside? 🤓
            </MessageBubble>

            <MessageBubble sender="assistant" timestamp="just now">
              <div>Of course! For you identity nerds out there 🤓💻</div>
              <br />
              <Button
                onClick={() => setShowJson(!showJson)}
                style={{
                  fontSize: defaultTheme.fontSize.sm,
                  padding: `${defaultTheme.spacing.sm} ${defaultTheme.spacing.md}`,
                  backgroundColor: colors.secondaryBg,
                  color: colors.secondaryText,
                  border: `1px solid ${colors.primaryBorder}`,
                }}
              >
                {showJson ? '🙈 Hide JSON' : '👀 Show JSON'}
              </Button>
              {showJson && (
                <div
                  style={{
                    marginTop: defaultTheme.spacing.md,
                    padding: defaultTheme.spacing.md,
                    backgroundColor: colors.secondaryBg,
                    border: `1px solid ${colors.primaryBorder}`,
                    borderRadius: '10px',
                    overflow: 'auto',
                    maxHeight: '300px',
                  }}
                >
                  <pre
                    style={{
                      margin: 0,
                      fontSize: defaultTheme.fontSize.xs,
                      color: colors.primaryText,
                      fontFamily: defaultTheme.fontFamily.mono,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {JSON.stringify(identityData, null, 2)}
                  </pre>
                </div>
              )}
            </MessageBubble>
          </div>

          {/* Chat Input (non-functional, just for aesthetics) */}
          <div
            style={{
              borderTop: `1px solid ${colors.primaryBorder}`,
              padding: defaultTheme.spacing.md,
              backgroundColor: colors.secondaryBg,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: defaultTheme.spacing.sm,
                padding: defaultTheme.spacing.sm,
                backgroundColor: colors.primaryBg,
                border: `1px solid ${colors.primaryBorder}`,
                borderRadius: defaultTheme.borderRadius.md,
              }}
            >
              <span
                style={{
                  color: colors.primaryAccent,
                  fontSize: defaultTheme.fontSize.md,
                }}
              >
                {'>'}
              </span>
              <input
                type="text"
                placeholder={isMobile ? "enter your message" : "Type or speak your message"}
                disabled
                style={{
                  flex: 1,
                  backgroundColor: 'transparent',
                  border: 'none',
                  outline: 'none',
                  color: colors.tertiaryText,
                  fontFamily: 'var(--font-departure-mono)',
                  fontSize: isMobile ? '12px' : defaultTheme.fontSize.md,
                }}
              />
              <button
                disabled
                style={{
                  padding: isMobile ? '8px 12px' : '4px 10px',
                  backgroundColor: colors.primaryAccent,
                  color: colors.primaryBg,
                  border: 'none',
                  borderRadius: defaultTheme.borderRadius.sm,
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'not-allowed',
                  opacity: 0.6,
                }}
              >
                SEND
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
